import {
    useCallback,
    useMemo,
    useState,
} from 'react'
import noop from 'lodash/noop'
import isEqual from 'lodash/isEqual'
import {
    useAuth,
} from 'app/Auth'

import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useHasPermission from 'app/hooks/useHasPermission'
import FormWithControls from 'app/shared-components/FormWithControls'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    RequestConfig,
} from 'app/types/request.types'
import useLabel from 'app/hooks/useLabel'

import {
    ActivityRecord,
} from '../activityRecords.types'
import getActivityRecordFields from './ActivityRecord.fields'
import useStyles from './ActivityRecord.style'

type Props = {
    data?: ActivityRecord,
    onSuccess?: (props: any) => void,
    onCancel?: (...args: any[]) => void,
    requestParam?: RequestConfig,
    disabled?: boolean,
    customButtons?: JSX.Element[],
}

const EMPTY_OBJECT = {} as ActivityRecord
const EMPTY_ARRAY = []

const ActivityRecordForm = ({
    data = EMPTY_OBJECT,
    onSuccess = noop,
    onCancel = noop,
    requestParam,
    disabled = false,
    customButtons = EMPTY_ARRAY,
}: Readonly<Props>) => {
    const l = useLabel()
    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()
    const {
        user,
    } = useAuth()

    const [
        value,
        setValue,
    ] = useState(data)

    const hasRelationOpsPermission = useHasPermission(['relationships_ops'])
    const hasRelationSalesPermission = useHasPermission(['relationships_sales'])

    const currentUserName = useMemo(() => {
        if (!user) {
            return ''
        }
        const {
            firstName,
            lastName,
        } = user

        return `${firstName} ${lastName}`
    }, [user])

    const participantsTypes = useMemo(() => {
        let filterType = []

        if (hasRelationOpsPermission) {
            filterType = ['OPS_PROCUREMENT']
        }
        if (hasRelationSalesPermission) {
            filterType = [
                ...filterType,
                'SKYCELL_COMMERCIAL',
            ]
        }
        return filterType
    }, [
        hasRelationOpsPermission,
        hasRelationSalesPermission,
    ])

    const exists = !!data?.id

    const fields = useMemo(() => {
        return getActivityRecordFields({
            getAllowedValues,
            exists,
            organizerName: currentUserName,
            companies: (value.companies || []).map(({
                id,
            }) => {
                return id
            }),
            participantsTypes,
            disabled,
            l,
        })
    }, [
        getAllowedValues,
        exists,
        currentUserName,
        value.companies,
        participantsTypes,
        disabled,
        l,
    ])

    const fieldsWithClasses = useFieldsWithClassName(
        fields,
        classes,
    )

    const onChange = useCallback((newValues) => {
        setValue((prev) => {
            if (!isEqual(newValues.companies, prev.companies)) {
                return {
                    ...prev,
                    ...newValues,
                    participants: [],
                }
            }
            return {
                ...prev,
                ...newValues,
            }
        })
    }, [])

    return (
        <FormWithControls
            onSuccess={onSuccess}
            value={value}
            name="ActivityRecord"
            setExternalValue={onChange}
            fields={fieldsWithClasses}
            requestParams={requestParam}
            exists={exists}
            onCancel={onCancel}
            disabled={disabled}
            classNames={{
                gridWrapper: classes.gridWrapper,
                gridCardWrapper: classes.gridCardWrapper,
            }}
            customButtons={customButtons}
        />
    )
}

export default ActivityRecordForm
